import { Navigate, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { QueryTarget } from "@/shared/domain/query";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { DatasetTab, Goto } from "@/shared/navigation";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import { featureFlagRoute } from "@/shared/routing/featureFlagRoute";
import { InheritOutletContext } from "@/shared/routing/InheritOutletContext";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { CreateDataset } from "./CreateDataset";
import { CreateDatasetEvent } from "./CreateDatasetEvent";
import { Dataset } from "./Dataset";
import { DatasetAgentThreads } from "./Dataset/DatasetAgentThreads";
import { DatasetDetails } from "./Dataset/DatasetDetails";
import { DatasetEvents } from "./Dataset/DatasetEvents";
import { DatasetFileBrowser } from "./Dataset/DatasetFileBrowser";
import { DatasetInvocations } from "./Dataset/DatasetInvocations";
import { DatasetPermissions } from "./Dataset/DatasetPermissions";
import { DatasetSessions } from "./Dataset/DatasetSessions";
import { DatasetAISummary } from "./Dataset/DatasetSummary";
import { DatasetTopics } from "./Dataset/DatasetTopics";
import { DatasetsList } from "./DatasetsList";
import { loadDatasetByName } from "./loaders";

// The list blocks on two independent reads: its saved table preferences, and —
// only when the URL names one — the View it should open with. Resolving the
// View here is what lets `useViewFromLink` build the table from it on the
// first render, instead of the table arriving unfiltered and being corrected.
const datasetsListLoader = composeLoaders(
  loadSearchTablePreferences(queryClient, "dataset_search_table_preferences"),
  makeViewsLoader(queryClient, { target: QueryTarget.Datasets }),
);

export const DatasetsRoutes = () => [
  <Route
    key={"/"}
    index
    loader={datasetsListLoader}
    element={<DatasetsList />}
  />,
  <Route key={"create"} path="create" element={<CreateDataset />} />,
  <Route
    key={"name"}
    path="name/:datasetName"
    loader={loadDatasetByName}
    shouldRevalidate={({ currentUrl, nextUrl }) => {
      return currentUrl.pathname !== nextUrl.pathname;
    }}
    // If the loader does not redirect to a dataset,
    // take the user to the dataset search page.
    element={<Navigate to="/search" replace />}
  />,
  <Route
    key={"id/events/create"}
    path=":datasetPath/events/create"
    element={<CreateDatasetEvent />}
  />,
  // Dataset detail view: parent layout with one child route per tab,
  // mounted via <Dataset />'s <Outlet>. <Dataset /> publishes the
  // dataset record, current org, and datasetId through outlet context,
  // which tabs read via `useDatasetContext` instead of re-deriving from
  // useParams/useAuth.
  <Route key={"id"} path=":datasetPath" element={<Dataset />}>
    <Route index element={<Navigate to={DatasetTab.Files} replace />} />
    <Route path={`${DatasetTab.Files}/*`} element={<DatasetFileBrowser />} />
    {/*
        Opening the Sessions tab must have saved column order / page size
        in cache before <DatasetSessions /> mounts: `useResourceView`
        snapshots `preferences` once via `React.useState` and never
        re-reads. `featureFlagRoute` already uses its loader slot for the
        flag gate, so this pathless wrapper Route runs the preferences
        loader; <InheritOutletContext /> forwards <Dataset />'s outlet
        context through to the gated child.
    */}
    <Route
      loader={loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      )}
      element={<InheritOutletContext />}
    >
      {featureFlagRoute({
        path: DatasetTab.Sessions,
        flagName: "release-sessions",
        redirectTo: ({ params }) =>
          Goto.createDatasetTabRoute(
            params.datasetPath ?? "",
            DatasetTab.Files,
          ),
        element: <DatasetSessions />,
      })}
    </Route>
    <Route path={DatasetTab.Topics} element={<DatasetTopics />} />
    <Route path={DatasetTab.Events} element={<DatasetEvents />} />
    <Route path={DatasetTab.Actions} element={<DatasetInvocations />} />
    <Route path={DatasetTab.AgentThreads} element={<DatasetAgentThreads />} />
    <Route path={DatasetTab.Details} element={<DatasetDetails />} />
    <Route path={DatasetTab.Permissions} element={<DatasetPermissions />} />
    <Route path={DatasetTab.AISummary} element={<DatasetAISummary />} />
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ datasetPath }) =>
            Goto.createDatasetTabRoute(datasetPath ?? "", DatasetTab.Files)
          }
        />
      }
    />
  </Route>,
];
